import { observer } from 'mobx-react'
import React from 'react'
import cn from 'classnames'

import {
   Button,
   Hierarchy,
   IconButton,
   IconButtonHierarchy,
   IconButtonSubVariant,
   SubVariant
} from '@flowwai-frontend/design-system'
import {
   cmdOrCtrl,
   openHomeRouteInNewTab
} from '@flowwai-frontend/bps-officer-web/common/ui'
import { Config } from '@flowwai-frontend/bps-officer-web/env-config'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import { useCustomT } from '@flowwai-frontend/shared/common-components'
import { SearchSmIcon } from '@flowwai-frontend/design-system/icons'
import { useClientConfig } from '@flowwai-frontend/bps-officer-web/iam'

import { APP_CLIENT_ENUM } from '../../constants/NavItemConstants'

import * as Styles from './styles'

import './index.css'

interface Props {
   onClickSearch: () => void
   hideSearch?: boolean
}

const NavbarHeader = (props: Props): React.ReactElement => {
   const t = useCustomT('navbar:header')

   const isOfficerPortalConfig = APP_CLIENT_ENUM === 'MA&UD_ADMIN'

   const { theme } = useAppTheme()
   const { header: navItemTheme } = theme.navbar
   const { navbar: clientNavbarConfig } = useClientConfig()

   const renderSearchButtonRightIcon = (): React.ReactNode => (
      <div
         className={cn(
            Styles.shortcutContainerClassName,
            navItemTheme.searchShortcutBgColor,
            navItemTheme.searchShortcutTextColor,
            navItemTheme.searchShortcutBorderColor
         )}
      >
         {t('searchShortcut', { shortcut: cmdOrCtrl })}
      </div>
   )

   const renderSearchButton = (): React.ReactElement => (
      <Button
         hierarchy={Hierarchy.Secondary}
         size={'Small'}
         subVariant={SubVariant.GrayOutline}
         onClick={props.onClickSearch}
         leftIcon={({ fill }) => <SearchSmIcon height={20} width={20} />}
         rightIcon={renderSearchButtonRightIcon}
         data-testid='officer-nav-search-button'
         className={cn(
            Styles.searchButtonContainerStyles,
            navItemTheme.searchButtonBgColor,
            'global-search-button'
         )}
      >
         <p
            className={cn(Styles.searchClassName, navItemTheme.searchTextColor)}
         >
            {t('search')}
         </p>
      </Button>
   )

   const renderSearchIconButton = (): React.ReactElement => (
      <IconButton
         hierarchy={IconButtonHierarchy.Tertiary}
         subVariant={IconButtonSubVariant.Gray}
         size={'Medium'}
         onClick={props.onClickSearch}
         className='p-[4px]'
         icon={() => (
            <SearchSmIcon
               width={24}
               height={24}
               strokeWidth={2}
               stroke={navItemTheme.searchIconColor}
            />
         )}
         title={t('searchShortcut', { shortcut: cmdOrCtrl })}
      />
   )

   const renderSearchAction = (): React.ReactElement | null => {
      if (props.hideSearch) return null
      return Config.ENABLE_GLOBAL_SEARCH === 'true'
         ? renderSearchButton()
         : null
   }

   return (
      <div className={Styles.containerClassName}>
         <img
            src={clientNavbarConfig.logoURL}
            alt={t('logoAltText')}
            className={cn(Styles.imageClassName, {
               '!w-full': isOfficerPortalConfig
            })}
            onClick={openHomeRouteInNewTab}
         />
         {renderSearchAction()}
      </div>
   )
}

export default observer(NavbarHeader)
